import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  IconCheck,
  IconLayoutSidebarLeftCollapse,
  IconPencil,
  IconPlus,
  IconStar,
  IconStarFilled,
  IconTrash,
  IconX,
} from '@tabler/icons-react'
import classNames from 'classnames'
import { isEmpty, orderBy } from 'lodash'
import { format, isToday, isYesterday } from 'date-fns'

import { Button } from '@/components/buttons/Button'
import { TextInput } from '@/components/forms/TextInput'
import { SpinningLoader } from '@/components/interactions/SpinningLoader'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { ActionsDropdown } from '@/components/dropdowns/ActionsDropdown'

import { useKodiSessions } from '../hooks/useKodiSessions'
import { useKodiSessionMutations } from '../hooks/useKodiSessionMutations'
import { sessionDisplayName } from '../kodi.constants'
import { OpenKodiSidebarIcon } from '../OpenKodiSidebarIcon'
import { KodiMarkIcon } from '../KodiMarkIcon'

const formatRelativeDate = (iso) => {
  const date = new Date(iso)
  if (isToday(date)) {
    return 'Today'
  }
  if (isYesterday(date)) {
    return 'Yesterday'
  }
  return format(date, 'MMM d')
}

export const KodiSidebar = ({
  open,
  onToggle,
  onNewChat,
  onSelectSession,
  onActiveSessionDeleted,
}) => {
  const { id: activeSessionId } = useParams()
  const { sessions, loading, error } = useKodiSessions({ enabled: open })
  const { rename, remove, toggleFavorite } = useKodiSessionMutations()
  const [editingId, setEditingId] = useState(null)
  const [editingValue, setEditingValue] = useState('')
  const [pendingIds, setPendingIds] = useState([])

  const trackPending = (sessionId, promise) => {
    setPendingIds((prior) => [...prior, sessionId])
    promise
      .finally(() => {
        setPendingIds((prior) => prior.filter((id) => id !== sessionId))
      })
      // Mutations already toasted and rolled back; swallow so the tracked
      // promise doesn't surface a second, unhandled rejection.
      .catch(() => {})
  }

  const startRename = (session) => {
    setEditingId(session.id)
    setEditingValue(session.name || '')
  }

  const cancelRename = () => {
    setEditingId(null)
    setEditingValue('')
  }

  const submitRename = (session) => {
    const trimmed = editingValue.trim()
    cancelRename()
    if (!trimmed || trimmed === session.name) {
      return
    }
    trackPending(session.id, rename(session, trimmed))
  }

  const submitDelete = (session) => {
    const promise = remove(session)
    trackPending(session.id, promise)
    if (session.session_id === activeSessionId) {
      promise
        .then(() => {
          onActiveSessionDeleted()
        })
        .catch(() => {})
    }
  }

  const handleEditKeyDown = (event, session) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      submitRename(session)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      cancelRename()
    }
  }

  if (!open) {
    return (
      <div className="kodi-sidebar__toggle-pill" data-testid="kodi-sidebar-toggle">
        <InteractiveTooltip content="Open chats" placement="right">
          <Button
            basic
            circular
            small
            aria-label="Open chats"
            onClick={onToggle}
            icon={<OpenKodiSidebarIcon />}
          />
        </InteractiveTooltip>
        <InteractiveTooltip content="New chat" placement="right">
          <Button
            basic
            circular
            small
            aria-label="Start new chat"
            onClick={onNewChat}
            icon={<IconPlus stroke={1.5} />}
          />
        </InteractiveTooltip>
      </div>
    )
  }

  return (
    <aside className="kodi-sidebar" data-testid="kodi-sidebar">
      <div className="kodi-sidebar__header">
        <div className="kodi-sidebar__identity">
          <span className="kodi-sidebar__mark-tile">
            <KodiMarkIcon size={16} />
          </span>
          <span className="kodi-sidebar__title">Kodi</span>
        </div>
        <div className="kodi-sidebar__header-actions">
          <InteractiveTooltip content="New chat" placement="bottom">
            <Button
              basic
              square
              small
              aria-label="Start new chat"
              onClick={onNewChat}
              icon={<IconPlus stroke={1.5} />}
            />
          </InteractiveTooltip>
          <InteractiveTooltip content="Collapse chats" placement="bottom">
            <Button
              basic
              square
              small
              aria-label="Close sidebar"
              onClick={onToggle}
              icon={<IconLayoutSidebarLeftCollapse stroke={1.5} />}
            />
          </InteractiveTooltip>
        </div>
      </div>

      <div className="kodi-sidebar__list" data-testid="kodi-sidebar-list">
        {loading && isEmpty(sessions) && (
          <div className="kodi-sidebar__placeholder">
            <SpinningLoader size={20} />
          </div>
        )}
        {!loading && error && (
          <div className="kodi-sidebar__placeholder kodi-sidebar__placeholder--error">
            Couldn’t load chats
          </div>
        )}
        {!loading && !error && isEmpty(sessions) && (
          <div className="kodi-sidebar__placeholder">No chat history</div>
        )}
        {!isEmpty(sessions) &&
          // Mirror the server ordering (favorites first, then newest) so a
          // client-side toggle lands a row where a reload would put it.
          orderBy(
            sessions,
            [
              (session) => Boolean(session.is_favorite),
              (session) => new Date(session.created_at).getTime(),
              'id',
            ],
            ['desc', 'desc', 'desc']
          ).map((session) => (
            <div
              key={session.id}
              data-testid={`kodi-sidebar-item-row-${session.id}`}
              className={classNames('kodi-sidebar__item', {
                'kodi-sidebar__item--active': session.session_id === activeSessionId,
                'kodi-sidebar__item--pending': pendingIds.includes(session.id),
              })}
            >
              {editingId === session.id ? (
                <div className="kodi-sidebar__item-rename-wrap">
                  <TextInput
                    small
                    autoFocus
                    aria-label="Chat name"
                    className="kodi-sidebar__item-rename"
                    data-testid={`kodi-sidebar-item-rename-input-${session.id}`}
                    value={editingValue}
                    onChange={(event) => setEditingValue(event.target.value)}
                    onBlur={cancelRename}
                    onKeyDown={(event) => handleEditKeyDown(event, session)}
                  />
                  <Button
                    brand
                    small
                    square
                    aria-label="Save name"
                    data-testid={`kodi-sidebar-item-rename-submit-${session.id}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => submitRename(session)}
                    icon={<IconCheck />}
                  />
                  <Button
                    small
                    square
                    aria-label="Cancel rename"
                    data-testid={`kodi-sidebar-item-rename-cancel-${session.id}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={cancelRename}
                    icon={<IconX />}
                  />
                </div>
              ) : (
                <button
                  type="button"
                  data-testid={`kodi-sidebar-item-${session.id}`}
                  className="kodi-sidebar__item-select"
                  onClick={() => onSelectSession(session.session_id, sessionDisplayName(session))}
                >
                  {session.is_favorite && (
                    <IconStarFilled
                      size={12}
                      className="fill-warning kodi-sidebar__item-star"
                      data-testid={`kodi-sidebar-item-star-${session.id}`}
                    />
                  )}
                  <span className="kodi-sidebar__item-name">{sessionDisplayName(session)}</span>
                  <span className="kodi-sidebar__item-date">
                    {formatRelativeDate(session.created_at)}
                  </span>
                </button>
              )}
              {pendingIds.includes(session.id) && (
                <div
                  className="kodi-sidebar__item-loader"
                  data-testid={`kodi-sidebar-item-loader-${session.id}`}
                >
                  <SpinningLoader size={14} />
                </div>
              )}
              {editingId !== session.id && !pendingIds.includes(session.id) && (
                <div className="kodi-sidebar__item-actions">
                  <ActionsDropdown
                    dataTestId={`kodi-sidebar-item-actions-${session.id}`}
                    actions={[
                      {
                        label: session.is_favorite ? 'Unfavorite' : 'Favorite',
                        icon: session.is_favorite ? (
                          <IconStarFilled size={16} className="fill-warning" />
                        ) : (
                          <IconStar size={16} />
                        ),
                        onClick: () => trackPending(session.id, toggleFavorite(session)),
                        testId: `kodi-sidebar-item-favorite-${session.id}`,
                      },
                      {
                        label: 'Rename',
                        icon: <IconPencil size={16} />,
                        onClick: () => startRename(session),
                        testId: `kodi-sidebar-item-rename-${session.id}`,
                      },
                      {
                        label: 'Delete',
                        icon: <IconTrash size={16} />,
                        isDanger: true,
                        onClick: () => submitDelete(session),
                        testId: `kodi-sidebar-item-delete-${session.id}`,
                      },
                    ]}
                  />
                </div>
              )}
            </div>
          ))}
      </div>
    </aside>
  )
}
